import { __awaiter, __generator } from "tslib";
import { fetchCatalog, fetchSeasonShardJson, resolveLatestSeasonFromCatalog, } from './catalogApi';
import { packsSeasonCompactIndexUrl, seasonPreviewMediaConfig } from './mediaConfig';
import { SEASON_PREVIEW_IMAGE_MAP_KEY, SEASON_PREVIEW_LAST_SEASON_KEY, SEASON_PREVIEW_SHARD_CACHE_PREFIX, } from './constants';
import { normalizePacksIndexToImageMap } from './packsIndexNormalize';
var SHARD_CACHE_TTL_MS = 15 * 60 * 1000;
function seasonCacheKey(seasonCode) {
    return String(seasonCode || '').trim().toUpperCase();
}
function readLocalJson(key) {
    try {
        var raw = localStorage.getItem(key);
        if (!raw) {
            return undefined;
        }
        return JSON.parse(raw);
    }
    catch (_a) {
        return undefined;
    }
}
function writeLocalJson(key, value) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    }
    catch (_a) {
        /* ignore quota */
    }
}
function readShardCache(shardUrl) {
    try {
        var raw = sessionStorage.getItem(SEASON_PREVIEW_SHARD_CACHE_PREFIX + shardUrl);
        if (!raw) {
            return null;
        }
        var parsed = JSON.parse(raw);
        if (!parsed || Date.now() - parsed.at > SHARD_CACHE_TTL_MS) {
            return null;
        }
        return parsed.data;
    }
    catch (_a) {
        return null;
    }
}
function writeShardCache(shardUrl, data) {
    try {
        sessionStorage.setItem(SEASON_PREVIEW_SHARD_CACHE_PREFIX + shardUrl, JSON.stringify({ at: Date.now(), data: data }));
    }
    catch (_a) {
        /* ignore */
    }
}
function fetchCompactIndexFallback(seasonCode) {
    return __awaiter(this, void 0, void 0, function () {
        var key, imageMaps, url, response, data, map;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    key = seasonCacheKey(seasonCode);
                    imageMaps = readLocalJson(SEASON_PREVIEW_IMAGE_MAP_KEY) || {};
                    if (imageMaps[key]) {
                        return [2 /*return*/, imageMaps[key]];
                    }
                    url = packsSeasonCompactIndexUrl(seasonCode);
                    return [4 /*yield*/, fetch(url, { credentials: 'omit' })];
                case 1:
                    response = _a.sent();
                    if (!response.ok) {
                        throw new Error('Compact index HTTP ' + response.status);
                    }
                    return [4 /*yield*/, response.json()];
                case 2:
                    data = _a.sent();
                    map = normalizePacksIndexToImageMap(data);
                    imageMaps[key] = map;
                    writeLocalJson(SEASON_PREVIEW_IMAGE_MAP_KEY, imageMaps);
                    return [2 /*return*/, map];
            }
        });
    });
}
function fetchShardImageMap(shardUrl) {
    return __awaiter(this, void 0, void 0, function () {
        var cached, data;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    cached = readShardCache(shardUrl);
                    if (cached) {
                        return [2 /*return*/, normalizePacksIndexToImageMap(cached)];
                    }
                    return [4 /*yield*/, fetchSeasonShardJson(shardUrl)];
                case 1:
                    data = _a.sent();
                    writeShardCache(shardUrl, data);
                    return [2 /*return*/, normalizePacksIndexToImageMap(data)];
            }
        });
    });
}
/** Hent billeder via catalog.json → season shard (fallback: assets compact index). */
export function fetchSeasonPreviewImageSource(seasonCodeHint) {
    return __awaiter(this, void 0, void 0, function () {
        var code_1, catalog, entry, shardUrl, imageMap_1, _a, fallbackMap, catalog, resolved, imageMap_2, e_1, fallbackSeason, imageMap;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!seasonCodeHint) return [3 /*break*/, 8];
                    code_1 = seasonCacheKey(seasonCodeHint);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, fetchCatalog()];
                case 2:
                    catalog = _b.sent();
                    entry = catalog.seasons.find(function (s) { return String(s.season).toUpperCase() === code_1 && s.shard_key; });
                    if (!entry) return [3 /*break*/, 4];
                    shardUrl = seasonPreviewMediaConfig.staticBaseUrl +
                        String(entry.shard_key).replace(/^\/+/, '');
                    return [4 /*yield*/, fetchShardImageMap(shardUrl)];
                case 3:
                    imageMap_1 = _b.sent();
                    if (Object.keys(imageMap_1).length) {
                        return [2 /*return*/, { seasonCode: code_1, imageMap: imageMap_1 }];
                    }
                    _b.label = 4;
                case 4: return [3 /*break*/, 6];
                case 5:
                    _a = _b.sent();
                    return [3 /*break*/, 6];
                case 6: return [4 /*yield*/, fetchCompactIndexFallback(code_1)];
                case 7:
                    fallbackMap = _b.sent();
                    return [2 /*return*/, { seasonCode: code_1, imageMap: fallbackMap }];
                case 8:
                    _b.trys.push([8, 11, , 12]);
                    return [4 /*yield*/, fetchCatalog()];
                case 9:
                    catalog = _b.sent();
                    resolved = resolveLatestSeasonFromCatalog(catalog);
                    return [4 /*yield*/, fetchShardImageMap(resolved.shardUrl)];
                case 10:
                    imageMap_2 = _b.sent();
                    if (Object.keys(imageMap_2).length) {
                        return [2 /*return*/, { seasonCode: resolved.seasonCode, imageMap: imageMap_2 }];
                    }
                    return [3 /*break*/, 12];
                case 11:
                    e_1 = _b.sent();
                    console.warn('[SeasonPreview] catalog/shard fetch failed, trying fallback', e_1);
                    return [3 /*break*/, 12];
                case 12:
                    fallbackSeason = seasonCacheKey(seasonPreviewMediaConfig.fallbackSeason);
                    return [4 /*yield*/, fetchCompactIndexFallback(fallbackSeason)];
                case 13:
                    imageMap = _b.sent();
                    return [2 /*return*/, { seasonCode: fallbackSeason, imageMap: imageMap }];
            }
        });
    });
}
export function persistLastSeasonCode(seasonCode) {
    var code = seasonCacheKey(seasonCode);
    if (!code) {
        return;
    }
    try {
        localStorage.setItem(SEASON_PREVIEW_LAST_SEASON_KEY, code);
    }
    catch (_a) {
        /* ignore */
    }
}
//# sourceMappingURL=packIndexFetch.js.map